import { NavigateFunction } from "react-router-dom";

export enum ErrorType {
  UNAUTHORIZED = 401,
  FORBIDDEN = 403,
  NOT_FOUND = 404,
  SERVER_ERROR = 503,
  NETWORK_ERROR = 0,
  ABORT = -1,
}

export interface ApiError {
  status: number;
  message: string;
  detail?: string;
}

export interface ErrorHandlerOptions {
  navigate: NavigateFunction;
  setAlertSetting: (setting: any) => void;
  setToastSetting?: (setting: any) => void;
}

/**
 * API 에러 핸들러
 *
 * 상태 코드에 따라 적절한 처리를 수행합니다.
 * - 401: 로그인 페이지로 리다이렉트
 * - 403: 결제 페이지로 리다이렉트
 * - 404: 리소스 없음 알림
 * - 503: 서버 오류 알림
 * - 0: 네트워크 오류 (토스트)
 * - -1: 사용자 취소 (조용히 처리)
 *
 * @param error - API 에러 객체
 * @param options - 에러 핸들링 옵션 (navigate, alert, toast)
 *
 * @example
 * ```tsx
 * try {
 *   const data = await fetchAPI();
 * } catch (error) {
 *   const errorType = getErrorType(error);
 *   handleApiError(
 *     { status: errorType, message: error.message },
 *     { navigate, setAlertSetting, setToastSetting }
 *   );
 * }
 * ```
 */
export const handleApiError = (
  error: ApiError,
  options: ErrorHandlerOptions
): void => {
  const { status, message, detail } = error;
  const { navigate, setAlertSetting, setToastSetting } = options;

  const defaultMessage = detail || message || "알 수 없는 오류가 발생했습니다.";

  switch (status) {
    case ErrorType.UNAUTHORIZED:
      setAlertSetting({
        alertTxt: `${defaultMessage}\n로그인 페이지로 이동하시겠습니까?`,
        double: true,
        btnTxt: "이동하기",
        onConfirm: () => {
          navigate("/login", {
            state: {
              is_completed: false,
              is_writed: false,
              from_chat: null,
              his_cnt: 0,
            },
          });
        },
      });
      break;

    case ErrorType.FORBIDDEN:
      setAlertSetting({
        alertTxt: `이용중인 상품이 없습니다.\n결제 페이지로 이동하시겠습니까?`,
        double: true,
        btnTxt: "이동하기",
        onConfirm: () => {
          navigate("/settings/products", {
            state: {
              is_completed: false,
              is_writed: false,
              from_chat: null,
              his_cnt: 0,
            },
          });
        },
      });
      break;

    case ErrorType.NOT_FOUND:
      setAlertSetting({
        alertTxt: `요청한 리소스를 찾을 수 없습니다.\n${defaultMessage}`,
        btnTxt: "확인",
      });
      break;

    case ErrorType.SERVER_ERROR:
      setAlertSetting({
        alertTxt: "서버 오류입니다.\n관리자에게 문의 바랍니다.",
        btnTxt: "닫기",
      });
      break;

    case ErrorType.NETWORK_ERROR:
      if (setToastSetting) {
        setToastSetting({
          warningTxt: "네트워크 연결을 확인해주세요.",
        });
      }
      break;

    case ErrorType.ABORT:
      // 사용자가 취소한 경우 - 조용히 처리
      console.log("Request aborted by user");
      break;

    default:
      setAlertSetting({
        alertTxt: `오류가 발생했습니다.\n${defaultMessage}`,
        btnTxt: "확인",
      });
  }
};

/**
 * 에러 타입 판별
 *
 * Error 객체로부터 ErrorType을 추출합니다.
 * - AbortError 또는 "cancel" 메시지: ABORT
 * - status 없고 오프라인: NETWORK_ERROR
 * - 그 외: status 값 또는 SERVER_ERROR
 *
 * @param error - 에러 객체
 * @returns ErrorType 열거형 값
 */
export const getErrorType = (error: any): ErrorType => {
  if (error.name === "AbortError" || /cancel/i.test(error.message)) {
    return ErrorType.ABORT;
  }

  if (!error.status && !navigator.onLine) {
    return ErrorType.NETWORK_ERROR;
  }

  return error.status || ErrorType.SERVER_ERROR;
};

/**
 * ApiError 생성 헬퍼
 *
 * @param status - HTTP 상태 코드
 * @param message - 에러 메시지
 * @param detail - 상세 정보 (선택)
 * @returns ApiError 객체
 */
export const createApiError = (
  status: number,
  message: string,
  detail?: string
): ApiError => ({
  status,
  message,
  detail,
});
